import React from 'react';
import { Mail, Linkedin, MapPin, Heart, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About', href: '#about' },
    { name: 'Skills', href: '#skills' },
    { name: 'Experience', href: '#experience' },
    { name: 'Articles', href: '#articles' },
    { name: 'Projects', href: '#projects' },
    { name: 'Contact', href: '#contact' }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-800">
      {/* Main Footer Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-white mb-4">
              Swastik Ranjan <span className="text-blue-400">Nanda</span>
            </h3>
            <p className="text-gray-400 mb-6 leading-relaxed max-w-md">
              Software Engineer passionate about building innovative tech solutions that make a positive impact. 
              Specializing in automation, AI integration, and system optimization with 6+ years of experience.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="mailto:swastik.nanda@gmail.com"
                className="bg-slate-800 hover:bg-blue-600 text-gray-400 hover:text-white p-3 rounded-lg transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/in/swastik-nanda-gen-ai?lipi=urn%3Ali%3Apage%3Ad_flagship3_profile_view_base_contact_details%3B9qf5H69rRCaxYGsmxyUf1w%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 hover:bg-blue-600 text-gray-400 hover:text-white p-3 rounded-lg transition-all duration-300 transform hover:scale-110 hover:shadow-lg hover:shadow-blue-500/25"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-400 hover:text-blue-400 transition-colors duration-300"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-center text-gray-400">
                <Mail className="w-4 h-4 mr-3 text-blue-400" />
                <span className="text-sm">swastik.nanda@gmail.com</span>
              </div>
              <div className="flex items-center text-gray-400">
                <MapPin className="w-4 h-4 mr-3 text-blue-400" />
                <span className="text-sm">Bhubaneswar, India</span>
              </div>
            </div>
            
            {/* Availability Status */}
            <div className="mt-6 p-3 bg-slate-800/50 border border-green-500/20 rounded-lg">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                <span className="text-green-400 text-sm font-medium">Available for opportunities</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            {/* Copyright */}
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <div className="flex items-center justify-center md:justify-start">
                <img 
                  src="/SwastikLogo.gif" 
                  alt="Swastik Nanda Logo" 
                  className="w-6 h-6 mr-2 rounded-full"
                />
                <p className="flex items-center">
                  © {currentYear} Swastik Ranjan Nanda. Made with 
                  <Heart className="w-4 h-4 text-red-500 mx-1" fill="currentColor" />
                  and cutting-edge tech.
                </p>
              </div>
            </div>

            {/* Back to Top */}
            <button
              onClick={scrollToTop}
              className="bg-slate-800 hover:bg-blue-600 text-gray-400 hover:text-white p-2 rounded-lg transition-all duration-300 transform hover:scale-110 flex items-center"
            >
              <ArrowUp className="w-4 h-4 mr-2" />
              <span className="text-sm">Back to Top</span>
            </button>
          </div>
        </div>

        {/* Additional Footer Info */}
        <div className="mt-8 text-center">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-lg p-4">
            <p className="text-gray-400 text-sm">
              <span className="text-blue-400 font-medium">Let's build the future together!</span> 
              {" "}Always open to discussing innovative projects, AI solutions, and exciting collaborations.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Elements (Optional decorative) */}
      <div className="absolute bottom-20 left-10 w-20 h-20 border border-blue-400/20 rounded-full animate-pulse opacity-20"></div>
      <div className="absolute bottom-32 right-16 w-16 h-16 border border-purple-400/20 rounded-full animate-pulse opacity-20 delay-1000"></div>
    </footer>
  );
};

export default Footer;